import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { Mail, Send, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui';
import { cn } from '../lib/utils';
import { useTenant } from '../contexts/TenantContext';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { apiWorker } from '../api/worker';
import toast from 'react-hot-toast';
import { SEO } from '../components/SEO';

export function ContactPage() {
  const { brand } = useTenant();
  const brandName = brand.name;
  const [searchParams] = useSearchParams();
  const isMigration = searchParams.get('topic') === 'migration';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState(
    isMigration
      ? "Hi OrgSets team, we would like assistance with a free concierge migration. We are currently using [Wild Apricot / MemberPlanet / Spreadsheets / Other] with approximately [X] members across [Y] chapters."
      : ""
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      const data = Object.fromEntries(formData.entries());
      
      await apiWorker.post('/api/public/contact', data);
      
      setIsSubmitted(true);
      toast.success(
        isMigration
          ? 'Migration request received! Our engineering team will review your requirements and reach out within 24 hours.'
          : 'Message sent successfully! Our team will be in touch shortly.'
      );
    } catch (error) {
      toast.error('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-white dark:bg-black text-black dark:text-white">
      <SEO 
        title={isMigration ? "Free Concierge Migration | OrgSets" : "Contact Us"} 
        description={
          isMigration
            ? "Request free concierge migration to OrgSets. Our team will migrate your rosters, dues history, and documents from Wild Apricot, MemberPlanet, or spreadsheets with zero downtime."
            : `Get in touch with the ${brandName} team for any inquiries about our organization management platform.`
        } 
      />
      <PublicHeader />
      
      <main className="pt-28 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Structural Container: 8px border on all sides */}
        <div className="border-8 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] bg-white dark:bg-black">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y-8 lg:divide-y-0 lg:divide-x-8 divide-black dark:divide-white">
            
            {/* Left Column: Headline and Info */}
            <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-between bg-black text-white dark:bg-white dark:text-black">
              <div>
                <span className="inline-block px-3 py-1 bg-[#FFD600] text-black font-mono font-bold text-xs uppercase tracking-widest border-2 border-black mb-6">
                  {isMigration ? 'Concierge Onboarding' : 'Direct Channel'}
                </span>
                <h1 className="text-5xl sm:text-6xl md:text-8xl font-black font-display uppercase tracking-tighter leading-[0.85] mb-8 break-words text-white dark:text-black">
                  {isMigration ? (
                    <>Migrate <br /> For Free.</>
                  ) : (
                    <>Get In <br /> Touch.</>
                  )}
                </h1>
                <p className="text-lg md:text-xl font-bold leading-relaxed mb-10 opacity-90">
                  {isMigration
                    ? 'Switching from Wild Apricot, MemberPlanet, or spreadsheets? Our migration engineers handle your rosters, past dues history, and chapter hierarchy with zero downtime.'
                    : 'Questions about scaling your multi-tier network? Our engineering team works directly with community directors and regional leaders.'}
                </p>

                <div className="border-l-4 border-[#0044FF] pl-4 py-2 mb-10 bg-white/5 dark:bg-black/5">
                  <p className="font-mono text-sm uppercase tracking-wider font-bold">
                    {isMigration ? 'Zero Downtime Guarantee' : 'Zero Fluff Guarantee'}
                  </p>
                  <p className="text-sm opacity-80 mt-1">
                    {isMigration 
                      ? 'Bulk roster imports, CSV normalization, and direct architectural support at no cost for qualified organizations.'
                      : 'Direct access to technical architects. Typical response time under 24 hours.'}
                  </p>
                </div>
              </div>

              <div className="border-2 border-black dark:border-white bg-[#0044FF] text-white p-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-black text-white flex items-center justify-center border-2 border-white shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="font-mono font-bold text-xs uppercase tracking-widest text-[#FFD600]">
                      {isMigration ? 'Migration Desk' : 'Inquiries & Support'}
                    </h2>
                    <p className="font-bold text-base mt-0.5">
                      {isMigration ? 'migration@orgsets.com' : 'contact@orgsets.com'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Interactive Form */}
            <div className="p-8 sm:p-12 lg:p-16 bg-white dark:bg-black text-black dark:text-white">
              {isMigration && (
                <div className="p-4 bg-[#FFD600] text-black border-2 border-black font-mono text-xs font-bold uppercase tracking-wider mb-6 flex items-start sm:items-center gap-3 shadow-[4px_4px_0px_#000]">
                  <div className="w-7 h-7 bg-black text-[#FFD600] flex items-center justify-center shrink-0 border border-black">
                    ★
                  </div>
                  <div>
                    <span className="block font-black">Free Concierge Migration Program</span>
                    <span className="text-[11px] opacity-90 normal-case font-semibold">
                      Tell us about your organization below. We will assign an engineer to import your data.
                    </span>
                  </div>
                </div>
              )}

              {isSubmitted ? (
                <div className="py-16 text-center flex flex-col items-center justify-center space-y-6">
                  <div className="w-20 h-20 bg-[#FFD600] text-black border-4 border-black dark:border-white flex items-center justify-center shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                    <CheckCircle2 size={44} className="stroke-[3]" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-black uppercase tracking-tight">
                      {isMigration ? 'Migration Request Queued' : 'Transmission Received'}
                    </h3>
                    <p className="font-mono text-sm opacity-80 mt-2">
                      {isMigration
                        ? 'Your migration specs have been dispatched to our onboarding team. Expect a response within 24h.'
                        : 'Inquiry queued with engineering. Response dispatching within 24h.'}
                    </p>
                  </div>
                  <Button 
                    type="button"
                    variant="primary" 
                    className="bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider py-4 px-8 rounded-none border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white transition-colors"
                    onClick={() => {
                      setIsSubmitted(false);
                      if (!isMigration) setMessage('');
                    }}
                  >
                    Send Another Dispatch
                  </Button>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <input type="hidden" name="topic" value={isMigration ? "migration" : "general"} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-mono font-bold uppercase tracking-widest block">First Name</label>
                      <input 
                        name="firstName" 
                        required 
                        type="text" 
                        className="w-full bg-white dark:bg-black border-2 border-black dark:border-white p-3 font-mono text-sm focus:bg-[#FFD600]/10 focus:border-[#0044FF] outline-none rounded-none text-black dark:text-white placeholder:text-gray-400" 
                        placeholder="John" 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-mono font-bold uppercase tracking-widest block">Last Name</label>
                      <input 
                        name="lastName" 
                        required 
                        type="text" 
                        className="w-full bg-white dark:bg-black border-2 border-black dark:border-white p-3 font-mono text-sm focus:bg-[#FFD600]/10 focus:border-[#0044FF] outline-none rounded-none text-black dark:text-white placeholder:text-gray-400" 
                        placeholder="Doe" 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-mono font-bold uppercase tracking-widest block">Work Email</label>
                    <input 
                      name="email" 
                      required 
                      type="email" 
                      className="w-full bg-white dark:bg-black border-2 border-black dark:border-white p-3 font-mono text-sm focus:bg-[#FFD600]/10 focus:border-[#0044FF] outline-none rounded-none text-black dark:text-white placeholder:text-gray-400" 
                      placeholder="john@company.com" 
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-mono font-bold uppercase tracking-widest block">Organization Type</label>
                    <select 
                      name="type" 
                      defaultValue={isMigration ? "Multi-Chapter Network" : "Non-Profit"}
                      className="w-full bg-white dark:bg-black border-2 border-black dark:border-white p-3 font-mono text-sm focus:bg-[#FFD600]/10 focus:border-[#0044FF] outline-none rounded-none text-black dark:text-white appearance-none cursor-pointer"
                    >
                      <option>Multi-Chapter Network</option>
                      <option>Club / League</option>
                      <option>HOA & Community Board</option>
                      <option>Non-Profit / Association</option>
                      <option>Professional Body</option>
                      <option>Hobbyist Group</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-mono font-bold uppercase tracking-widest block">
                      {isMigration ? 'Current Software & Scope' : 'Message'}
                    </label>
                    <textarea 
                      name="message" 
                      required 
                      rows={4} 
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full bg-white dark:bg-black border-2 border-black dark:border-white p-3 font-mono text-sm focus:bg-[#FFD600]/10 focus:border-[#0044FF] outline-none rounded-none text-black dark:text-white resize-none placeholder:text-gray-400" 
                      placeholder="How can we help?"
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="w-full bg-black text-white dark:bg-white dark:text-black hover:bg-[#0044FF] dark:hover:bg-[#0044FF] dark:hover:text-white font-black uppercase tracking-wider py-4 px-8 border-2 border-black dark:border-white rounded-none transition-colors shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Send className="w-4 h-4" />
                    {isSubmitting ? 'DISPATCHING...' : isMigration ? 'SUBMIT CONCIERGE MIGRATION REQUEST' : 'TRANSMIT MESSAGE'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
